import React, { useState, useEffect } from 'react'
import { FaGoogle, FaKey, FaMicrosoft } from 'react-icons/fa'
import { startAuthentication, WebAuthnAbortService } from '@simplewebauthn/browser'
import { Link, useNavigate, useLocation } from 'react-router'
import { Box, Button, Heading, Text, Input, Alert, FormControl, Loading, Stack, Icon } from '@/components/lib'
import useStore from '../../store'
import { api } from '../../api'
import utils from '../../utils/utils.js'

import useTitle from '../../hooks/useTitle'
import EntryContainer from '../includes/EntryContainer'

export default function Login ({ withWarning, invitationBanner }) {
  const store = useStore()
  const [loginToken, setLoginToken] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [otp, setOtp] = useState('')
  const [recoveryCode, setRecoveryCode] = useState('')
  const [useRecoveryCode, setUseRecoveryCode] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState()
  const [otpChallenge, setOtpChallenge] = useState(false)
  const [passkeyOptions, setPasskeyOptions] = useState(null)
  const navigate = useNavigate()
  const { search, pathname } = useLocation()
  useTitle('Login')

  // Conditional UI: if the browser supports it, kick off a passwordless
  // ceremony that lives in the background and resolves when the user picks a
  // passkey from the email-field autofill UI (GitHub-style single-click login).
  useEffect(() => {
    if (loginToken) return
    if (typeof window === 'undefined') return
    const PKC = window.PublicKeyCredential
    if (!PKC || typeof PKC.isConditionalMediationAvailable !== 'function') return
    let cancelled = false
    PKC.isConditionalMediationAvailable().then(available => {
      if (!available || cancelled) return
      api.accounts.getPasskeyAuthenticationOptions(async options => {
        if (cancelled) return
        try {
          const assertion = await startAuthentication({
            optionsJSON: options,
            useBrowserAutofill: true
          })
          if (cancelled) return
          login(null, { passkeyAssertion: assertion, passwordlessPasskey: true })
        } catch (err) {
          // Silent: cancelled by user, aborted by submit, or unsupported
        }
      }, () => {})
    }).catch(() => {})
    return () => {
      cancelled = true
      WebAuthnAbortService.cancelCeremony()
    }
  }, [loginToken])

  useEffect(() => {
    if (pathname !== '/login') return
    const params = new URLSearchParams(search)
    if (params?.get('token')) {
      setLoginToken(params.get('token'))
    }
  }, [search, pathname])

  useEffect(() => {
    if (loginToken) login()
  }, [loginToken])

  function login (e, overrides = {}) {
    e?.preventDefault?.()
    setLoading(true)
    setError()
    const passwordless = !!overrides.passwordlessPasskey
    api.accounts.login({
      loginToken: passwordless ? null : (loginToken || null),
      email: passwordless ? null : (email || null),
      password: passwordless ? null : (password || null),
      otp: overrides.otp ?? (passwordless || useRecoveryCode ? null : (otp || null)),
      recoveryCode: useRecoveryCode && !passwordless ? (recoveryCode || null) : null,
      passkeyAssertion: overrides.passkeyAssertion ?? null
    }, (data) => {
      setLoading(false)
      if (data.challenge === '2fa') setOtpChallenge(true)
      if (data.passkeyOptions) setPasskeyOptions(data.passkeyOptions)
      if (data.token) {
        store.completeLogin(data.token)
        if (data.usedRecoveryCode) {
          // A code has been spent, and the device it stood in for is
          // presumably gone: go straight to the place to set up a new one,
          // wherever the login form was shown. Not via postLoginRedirect,
          // which is only consumed on /login and may already be holding an
          // OAuth consent return.
          navigate('/settings/security?recovered=1')
          return
        }
        if (window.location.pathname === '/login') {
          // Pages that require a login before they can act (e.g. the OAuth
          // consent screen, which is entered by redirect from an external app)
          // stash where to come back to.
          const next = window.sessionStorage.getItem('postLoginRedirect')
          window.sessionStorage.removeItem('postLoginRedirect')
          // Must start with a single slash: '//evil.example.com' is a
          // protocol-relative URL and would send the user off-site after login.
          const safeNext = next && next.startsWith('/') && !next.startsWith('//')
          navigate(safeNext ? next : '/')
        }
      }
    }, (err) => {
      setError(err)
      setLoading(false)
    })
  }

  // Triggers the browser's passkey prompt and submits the resulting assertion
  // to /accounts/sessions. Used by both the 2FA path (options scoped to the
  // user, after email + password) and the passwordless path (discoverable
  // credential — server identifies the user from the assertion).
  async function authenticateWithPasskey (options = passkeyOptions, passwordless = false) {
    if (!options) return
    try {
      const assertion = await startAuthentication({ optionsJSON: options })
      login(null, { passkeyAssertion: assertion, passwordlessPasskey: passwordless })
    } catch (err) {
      if (err?.name !== 'NotAllowedError' && err?.name !== 'AbortError') {
        setError(err)
      }
    }
  }

  // Passwordless entry point: fetches discoverable-credential options, then
  // hands off to authenticateWithPasskey.
  function signInWithPasskey () {
    setError()
    api.accounts.getPasskeyAuthenticationOptions(
      options => authenticateWithPasskey(options, true),
      err => setError(err)
    )
  }

  function handleSSOLogin (provider) {
    if (provider === 'google') {
      utils.loginWithGoogle()
    }
    if (provider === 'microsoft') {
      utils.loginWithMicrosoft()
    }
  }

  if (loginToken) {
    return (
      <EntryContainer>
        {loading &&
          <Box>
            <Heading mb={2}>Logging you in...</Heading>
            <Text>Please wait while we check your account.</Text>
            <Box display='flex' justifyContent='center'>
              <Loading />
            </Box>
          </Box>}
        {error &&
          <Box>
            <Heading mb={2}>There was a problem logging you in</Heading>
            <Text>{error.message}</Text>

            <Text mt={10}>We recommend closing this tab and trying again.</Text>
          </Box>}
      </EntryContainer>
    )
  }

  return (
    <EntryContainer>
      <Stack gap={5}>
        {(withWarning || invitationBanner) &&
          <Box>
            <Alert status='info' variant='left-accent' rounded='md' shadow='md'>
              <Text>{invitationBanner || 'For security, you need to login to see this page.'}</Text>
            </Alert>
          </Box>}
        <Heading size='lg'>Login to Trialflare</Heading>
        {error &&
          <Alert status='warning' variant='left-accent' title={error.message} />}

        <Stack p={4} bg='bg.subtle' rounded='md'>
          <Heading size='sm'>Login with your organisation's single sign-on</Heading>
          <Stack direction={{ base: 'column', sm: 'row' }}>
            <Button colorPalette='primary' onClick={() => handleSSOLogin('google')}><Box as={FaGoogle} mr={2} /> Login with Google</Button>
            <Button colorPalette='primary' onClick={() => handleSSOLogin('microsoft')}><Box as={FaMicrosoft} mr={2} /> Login with Microsoft</Button>
          </Stack>
        </Stack>

        <Stack p={4} bg='bg.subtle' rounded='md'>
          <Heading size='sm'>Or login with a passkey</Heading>
          <Box>
            <Button colorPalette='primary' onClick={signInWithPasskey}><Box as={FaKey} mr={2} /> Choose a passkey</Button>
          </Box>
        </Stack>

        <Stack gap={5} p={4} borderColor='border.default' borderWidth='1px' rounded='md'>
          <Heading size='sm'>Or login with your email and password</Heading>

          <Stack as='form' gap={3} onSubmit={login}>
            <Box>
              <FormControl label='Email address'>
                <Input bg='bg.surface' autoFocus type='email' id='email' autoComplete='username webauthn' placeholder='jane@company.com' value={email} onChange={e => setEmail(e.target.value)} />
              </FormControl>
            </Box>
            <Box>
              <FormControl
                label='Password' helperText={
                  <Text>
                    <Link to='/password/forgotten'>Forgotten your password?</Link>
                  </Text>
      }
              >
                <Input bg='bg.surface' autoComplete='current-password' type='password' id='password' aria-describedby='password-helper-text' placeholder='Your password' value={password} onChange={e => setPassword(e.target.value)} />
              </FormControl>
            </Box>

            {(otpChallenge || passkeyOptions) &&
              <Stack mt={5} p={3} bg='bg.subtle'>
                <Box>
                  <Heading size='md'>Multi-factor authentication</Heading>
                </Box>
                {useRecoveryCode
                  ? (
                    <FormControl label='Enter a recovery code' helperText='One of the codes you saved when you set up two-factor authentication. Each works once.'>
                      <Input bg='bg.surface' autoFocus id='recoveryCode' autoComplete='off' placeholder='xxxxx-xxxxx' value={recoveryCode} onChange={e => setRecoveryCode(e.target.value)} />
                    </FormControl>)
                  : (
                    <Stack direction={['column', 'row']} gap={3}>
                      {otpChallenge &&
                        <FormControl label='Enter a one-time code' helperText='Enter the six-digit code generated by your device.'>
                          <Input bg='bg.surface' autoFocus type='number' id='otp' placeholder='123456' value={otp} onChange={e => setOtp(e.target.value)} />
                        </FormControl>}

                      {passkeyOptions &&
                        <FormControl label='Use a passkey'>
                          <Button type='button' onClick={() => authenticateWithPasskey()}><Icon as={FaKey} mr={2} /> Use a passkey</Button>
                        </FormControl>}
                    </Stack>)}
                <Box>
                  <Button type='button' size='xs' variant='ghost' onClick={() => setUseRecoveryCode(!useRecoveryCode)}>
                    {useRecoveryCode ? 'Use my device instead' : 'Lost your device? Use a recovery code'}
                  </Button>
                </Box>
              </Stack>}

            {error &&
              <Alert status='warning' variant='left-accent' title={error.message} />}
            <Button tabIndex='3' colorPalette='primary' type='submit' isLoading={loading}>Login</Button>
          </Stack>
        </Stack>
      </Stack>
    </EntryContainer>
  )
}
